import { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import FormLabel from '../../../components/forms/FormLabel';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { CommunicationService } from '../../../services/communication/communicationService';
import { DepartmentService } from '../../../services/department/departmentService';
import { ContractProfileLocationState } from '../../../utils/contractNavigation';
import { HiOutlineEye } from 'react-icons/hi2';

const columnOrder = [
    'contractCode',
    'targetDepartment',
    'studentName',
    'openedAt',
    'openingMessage',
    'closingMessage',
    'statusLabel',
];

const getResponseRows = (payload: any): any[] => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const getSelectRows = (payload: any): any[] => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const formatDateValue = (value: any): string => {
    if (!value) return '-';
    if (typeof value === 'string') return value;
    if (typeof value?.date === 'string') return value.date;
    return '-';
};

const CommunicationsPage = () => {
    const service = new CommunicationService();
    const { userCan } = usePrivilege();
    const location = useLocation();
    const navigate = useNavigate();
    const [openFilters, setOpenFilters] = useState(false);
    const canViewContracts = userCan('get_contracts', 'contracts');
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters({}, [{ field: 'openedAt', order: 'desc' }], 1, 25);

    const returnToCommunicationsPath = `${location.pathname}${location.search}`;
    const getCommunicationDetailPath = (communicationId: string) => `${menuRoutes.calls.path}${menuRoutes.calls.communications}/${communicationId}`;
    const getContractProfilePath = (contractId: string) => `${menuRoutes.contracts.path}/${contractId}/profile${menuRoutes.contracts.profile.info}`;

    const [response, loading, _error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listCommunications(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const [communicationDepartmentsResponse] = useFetch(useCallback(async () => {
        const departmentService = new DepartmentService();
        const apiResponse = await departmentService.listDepartmentsForSelect({
            page: 1,
            limit: 200,
            filter_filters: {
                handles_communications: true,
            },
            filter_order: [{ field: 'name', order: 'asc' }],
        });

        return apiResponse.getResponseData();
    }, []));

    const rows = getResponseRows(response);
    const communicationDepartments = getSelectRows(communicationDepartmentsResponse);
    const lastPage = response?.data?.lastPage ?? response?.lastPage ?? 1;
    const orderedData = () => rows.map((row: any) => ({
        id: row.id,
        contractCode: row.contract?.code || 'Sin contrato',
        targetDepartmentId: row.targetDepartment?.id || '',
        targetDepartment: row.targetDepartment?.name || row.targetDepartmentLegacy || 'N/A',
        studentName: [row.contract?.studentName, row.contract?.studentFirstName, row.contract?.studentLastName].filter(Boolean).join(' ') || 'Sin alumno',
        openedAt: formatDateValue(row.openedAt || row.createdAt),
        openingMessage: row.openingMessage || '',
        closingMessage: row.closingMessage || 'Pendiente',
        statusLabel: row.closedAt ? 'Cerrada' : 'Abierta',
        openedBy: [row.openedByUser?.name, row.openedByUser?.lastName].filter(Boolean).join(' ') || 'Sin emisor',
        closedBy: [row.closedByUser?.name, row.closedByUser?.lastName].filter(Boolean).join(' ') || 'Sin resolutor',
        closedAt: formatDateValue(row.closedAt),
        raw: row,
    }));

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    return (
        <Page container="fluid">
            <CustomTable
                id="communications-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[
                    'id',
                    'raw',
                    'targetDepartmentId'
                ]}
                overrideColumns={[
                    {
                        key: 'contractCode',
                        render: (row: any) => {
                            const contractId = row.row.original.raw?.contract?.id;
                            const contractProfileState: ContractProfileLocationState = {
                                fromCommunications: true,
                                returnToCommunicationsPath,
                            };

                            if (contractId && canViewContracts) {
                                return (
                                    <Link
                                        to={getContractProfilePath(contractId)}
                                        state={contractProfileState}
                                        className="inline-flex items-center gap-2"
                                    >
                                        <Text className="font-bold text-primary">{row.row.original.contractCode}</Text>
                                        <HiOutlineEye className="h-4 w-4 text-primary" size={18} />
                                    </Link>
                                );
                            }

                            return <Text className="font-bold text-primary">{row.row.original.contractCode}</Text>;
                        },
                    },
                    {
                        key: 'openedAt',
                        render: (row: any) => (
                            <div>
                                <div>{row.row.original.openedBy}</div>
                                <div className="text-xs text-gray-500">{row.row.original.openedAt}</div>
                            </div>
                        ),
                    },
                    {
                        key: 'openingMessage',
                        render: (row: any) => <div className="max-w-[22rem] truncate">{row.row.original.openingMessage}</div>,
                    },
                    {
                        key: 'closingMessage',
                        render: (row: any) => (
                            <div>
                                <div className="max-w-[22rem] truncate">{row.row.original.closingMessage}</div>
                                {row.row.original.closedAt && (
                                    <div className="text-xs text-gray-500">{row.row.original.closedBy} · {row.row.original.closedAt}</div>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'statusLabel',
                        render: (row: any) => (
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${row.row.original.statusLabel === 'Cerrada' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-800'}`}>
                                {row.row.original.statusLabel}
                            </span>
                        ),
                    },
                ]}
                actions={[
                    {
                        label: 'Ver',
                        permissions: { group: 'communications', permission: 'list_communications' },
                        render: (row: any) => {
                            return (
                                <Tooltip size="sm" content="Ver comunicación" placement="top" color="invert">
                                    <ActionIcon
                                        as="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate(getCommunicationDetailPath(row.id), {
                                            state: { returnToCommunicationsPath },
                                        })}
                                        className="hover:!border-gray-900 hover:text-gray-700"
                                    >
                                        <HiOutlineEye className="h-4 w-4" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            );
                        },
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.data?.totalRegisters || response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                toggleFilters={() => setOpenFilters(!openFilters)}
            >
                <Button variant="outline" onClick={() => refetch()}>
                    Actualizar
                </Button>
            </CustomTable>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle="Filtros comunicaciones">
                <FormLabel label="Departamento">
                    <select
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        value={filters.filter_filters?.target_department || ''}
                        onChange={(event) => updateFilters({ target_department: event.target.value || null })}
                    >
                        <option value="">Todos los departamentos</option>
                        {communicationDepartments.map((department: any) => (
                            <option key={department.id} value={department.id}>
                                {department.name}
                            </option>
                        ))}
                    </select>
                </FormLabel>
                <FormLabel label="Estado">
                    <select
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        value={filters.filter_filters?.is_closed ?? ''}
                        onChange={(event) => updateFilters({ is_closed: event.target.value === '' ? null : event.target.value })}
                    >
                        <option value="">Todos los estados</option>
                        <option value="false">Abiertas</option>
                        <option value="true">Cerradas</option>
                    </select>
                </FormLabel>
                <Button
                    size="sm"
                    variant="flat"
                    className="h-9 bg-gray-200/70"
                    onClick={() => resetFilters(25)}
                >
                    Limpiar
                </Button>
            </FilterDrawerView>
        </Page>
    );
};

export default CommunicationsPage;
